/**
 * North Bank — the anonymous fan thread, built to learn and discuss, not to
 * get popular. Appreciation only (no downvote, no heat badge, no per-author
 * score). Single-level replies, chronological. Open-moderation
 * stubs say when a take was hidden and why.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyNote, Rule } from '../components/chrome';
import { HandleEditor } from '../components/HandleEditor';
import { InTheBookCard } from '../components/Lore';
import { SafetySheet } from '../components/SafetySheet';
import { StatusToast } from '../components/StatusToast';
import {
  appreciate,
  buildTake,
  deleteTake,
  fallbackTakes,
  fetchParticipationStatus,
  fetchSafetyRequests,
  fetchReplies,
  fetchTakes,
  postReport,
  postTake,
} from '../lib/api';
import { consumeFailedReportDeliveries, readFailedSafetyDeliveries } from '../lib/outbox';
import type { FailedSafetyDelivery } from '../lib/outbox';
import type { ParticipationStatus, SafetyRequest, SafetyRequestKind } from '../lib/api';
import { FEEDS, useFeed } from '../lib/feeds';
import { mayPoll } from '../lib/public-reads';
import { CommunityActivityStatus, useCommunityAccess } from '../lib/community-access';
import { arsenalIsAway } from '../lib/score';
import { getHandle, shortHandle } from '../lib/handle';
import { timeAgo } from '../lib/heat';
import { broadcastFanReaction } from '../lib/intents';
import { northBankLink, share as shareMoment, type ShareMoment } from '../lib/share';
import type { Fixture, ReportReason, Take, TakeStub, Thread } from '../lib/types';
import { ParticipationGate, useParticipationConsent } from '../components/ParticipationGate';
import { announceRecoveredNorthBankTake, settleNorthBankDraft, subscribeToRecoveredNorthBankTakes, useNorthBankDraft } from './terrace-drafts';
import './terrace-mobile.css';

const MAX_TAKE = 280;
const HIDDEN_KEY = 'coyg_hidden_takes';

function localHidden(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function hideLocally(id: string): void {
  try {
    const next = [...localHidden().add(id)];
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(next.slice(-200)));
  } catch {
    /* private mode */
  }
}

// ── Reply row (single level) ──────────────────────────────────────────────────
const ReplyRow = ({ reply, mine, onDelete, onReport, participationAllowed, writesEnabled, onRequireParticipation }: { reply: Take; mine: boolean; onDelete: (id: string) => void; onReport: (id: string, reason: ReportReason) => void; participationAllowed: boolean; writesEnabled: boolean; onRequireParticipation: (action: string, onComplete: () => void, onSuccessFocus?: () => void) => void }) => {
  const [up, setUp] = useState(reply.up);
  const [on, setOn] = useState(reply.myVote === 'up');
  const [reporting, setReporting] = useState(false);
  const voteGeneration = useRef(0);
  const confirmedVote = useRef({ up: reply.up, myVote: reply.myVote });
  const [voteFailure, setVoteFailure] = useState(false);
  useEffect(() => { confirmedVote.current = { up: reply.up, myVote: reply.myVote }; setUp(reply.up); setOn(reply.myVote === 'up'); }, [reply.up, reply.myVote]);
  const commitAppreciation = () => {
    const generation = ++voteGeneration.current;
    setVoteFailure(false);
    const next = !on;
    setOn(next);
    setUp((u) => Math.max(0, u + (next ? 1 : -1)));
    void appreciate(reply.id, next).then((result) => {
      if (result) confirmedVote.current = result;
      if (generation === voteGeneration.current) {
        setOn(confirmedVote.current.myVote === 'up');
        setUp(confirmedVote.current.up);
        setVoteFailure(!result);
      }
    });
  };
  const tap = () => {
    if (!participationAllowed) {
      onRequireParticipation('appreciate this reply', commitAppreciation);
      return;
    }
    commitAppreciation();
  };
  return (
    <div className="reply">
      <div className="take-head">
        <span className="take-handle">
          <span className="reply-arrow">↳</span>
          {reply.handle}
          {mine && <span className="take-you">you</span>}
        </span>
        <span className="take-time">{timeAgo(reply.createdAt)}</span>
      </div>
      <p className="take-body">{reply.text}</p>
      <div className="take-foot">
        <button className={`appreciate${on ? ' on' : ''}`} onClick={tap} aria-label="Appreciate" disabled={!writesEnabled}>
          <span className="diamond">♦</span> {up.toLocaleString()}
        </button>
        {mine && (
          <button className="take-delete" onClick={() => onDelete(reply.id)}>
            Delete
          </button>
        )}
        {!mine && (
          <button className="report-btn" onClick={() => setReporting((open) => !open)} aria-label="Report reply">
            ⚑
          </button>
        )}
      </div>
      {voteFailure && <p className="composer-note" role="status">Appreciation wasn’t saved. Try again when COYG is available.</p>}
      {reporting && (
        <div className="report-row">
          {REPORT_REASONS.map((reason) => (
            <button key={reason.id} className="chip chip--small" onClick={() => { setReporting(false); onReport(reply.id, reason.id); }}>
              {reason.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Take card ────────────────────────────────────────────────────────────────
const REPORT_REASONS: Array<{ id: ReportReason; label: string }> = [
  { id: 'abuse', label: 'Abusive' },
  { id: 'spam', label: 'Spam' },
  { id: 'off-topic', label: 'Off topic' },
  { id: 'other', label: 'Something else' },
];

export const TakeCard = ({
  take,
  mine,
  myHandle,
  onAppreciate,
  onReported,
  onDelete,
  onShare,
  hiddenIds,
  participationAllowed,
  writesEnabled,
  threadReadOnly,
  onRequireParticipation,
  onHandleChange,
}: {
  take: Take;
  mine: boolean;
  myHandle: string;
  onAppreciate: (id: string) => void;
  onReported: (id: string, delivery: 'pending' | 'sent' | 'queued' | 'failed') => void;
  onDelete: (id: string) => void;
  onShare: (take: Take) => void;
  hiddenIds: Set<string>;
  participationAllowed: boolean;
  writesEnabled: boolean;
  threadReadOnly: boolean;
  onRequireParticipation: (action: string, onComplete: () => void, onSuccessFocus?: () => void) => void;
  onHandleChange: (handle: string) => void;
}) => {
  const [reporting, setReporting] = useState(false);
  const replyDraft = useNorthBankDraft(`reply:${take.id}`, null);
  const replyText = replyDraft.text;
  const setReplyText = replyDraft.setText;
  const [replying, setReplying] = useState(() => Boolean(replyDraft.text) && !replyDraft.pending);
  const [localReplyFailure, setReplyFailure] = useState(false);
  const replyFailure = localReplyFailure || replyDraft.failed;
  const [localReplyPending, setReplyPending] = useState(false);
  const replyPending = localReplyPending || replyDraft.pending;
  const replyLive = useRef(true);
  const [replyDeleteFailure, setReplyDeleteFailure] = useState(false);
  const [replyCountAuthority, setReplyCountAuthority] = useState<{ snapshot: string; count: number } | null>(null);
  const pendingReplyIds = useRef(new Set<string>());
  const unacknowledgedReplyIds = useRef(new Set<string>());
  const deletedReplyIds = useRef(new Set<string>());
  const previousReplySnapshot = useRef({ count: take.replyCount ?? take.replies?.length ?? 0, ids: new Set((take.replies ?? []).map((reply) => reply.id)) });
  const [replies, setReplies] = useState<Take[]>(() => (take.replies ?? []).filter((reply) => !hiddenIds.has(reply.id) && !localHidden().has(reply.id)));
  const replyTriggerRef = useRef<HTMLButtonElement>(null);
  const [allLoaded, setAllLoaded] = useState(() => {
    const initialReplies = take.replies?.length ?? 0;
    return (take.replyCount ?? initialReplies) <= initialReplies;
  });
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const hiddenReplyIds = new Set([...hiddenIds, ...localHidden(), ...deletedReplyIds.current]);
  const hiddenPreviewCount = (take.replies ?? []).filter((reply) => hiddenReplyIds.has(reply.id)).length;
  const replySnapshot = `${take.replyCount ?? take.replies?.length ?? 0}:${(take.replies ?? []).map((reply) => reply.id).join(',')}`;
  const latestReplySnapshot = useRef(replySnapshot);
  latestReplySnapshot.current = replySnapshot;
  const latestReplyPreviewIds = useRef(new Set<string>());
  latestReplyPreviewIds.current = new Set((take.replies ?? []).map((reply) => reply.id));
  // Full reads and successful local deletions are newer than the parent
  // snapshot. Keep their count until the parent publishes changed reply data.
  const serverCount = replyCountAuthority?.snapshot === replySnapshot
    ? replyCountAuthority.count
    : Math.max(0, (take.replyCount ?? take.replies?.length ?? 0) - hiddenPreviewCount);
  const previewReplies = repliesExpanded ? replies : replies.slice(0, 1);
  const hiddenLoadedReplies = Math.max(0, replies.length - previewReplies.length);
  const hiddenServerReplies = Math.max(0, serverCount - replies.length);
  const remainingReplies = hiddenLoadedReplies + hiddenServerReplies;
  const moreLabel =
    previewReplies.length > 0
      ? `Read ${remainingReplies} more ${remainingReplies === 1 ? 'reply' : 'replies'}`
      : `Read ${remainingReplies} ${remainingReplies === 1 ? 'reply' : 'replies'}`;

  useEffect(() => {
    replyLive.current = true;
    return () => { replyLive.current = false; };
  }, []);

  useEffect(() => {
    if (!replyDraft.pending && replyDraft.failed && replyDraft.text) setReplying(true);
  }, [replyDraft.pending, replyDraft.failed, replyDraft.text]);

  useEffect(() => subscribeToRecoveredNorthBankTakes((reply) => {
    if (reply.parentId !== take.id || hiddenIds.has(reply.id) || localHidden().has(reply.id) || deletedReplyIds.current.has(reply.id)) return;
    if (!latestReplyPreviewIds.current.has(reply.id)) unacknowledgedReplyIds.current.add(reply.id);
    else unacknowledgedReplyIds.current.delete(reply.id);
    setReplies((current) => [...current.filter((row) => row.id !== reply.id), reply].sort((left, right) => left.createdAt - right.createdAt));
    setRepliesExpanded(true);
  }), [take.id, hiddenIds]);

  useEffect(() => {
    const incoming = take.replies ?? [];
    setReplyCountAuthority((current) => current?.snapshot === replySnapshot ? current : null);
    const count = take.replyCount ?? incoming.length;
    const previous = previousReplySnapshot.current;
    const complete = count <= incoming.length;
    // A partial preview cannot identify which older row disappeared. If its
    // count/identity proves there were removals, retain only confirmed rows;
    // the existing Read more action can retrieve the remainder on demand.
    const newPreviewIds = incoming.filter((reply) => !previous.ids.has(reply.id)).length;
    const removals = count < previous.count || (incoming.length > 0 && newPreviewIds > Math.max(0, count - previous.count));
    const hidden = new Set([...hiddenIds, ...localHidden(), ...deletedReplyIds.current]);
    // A parent read already in flight when POST succeeded may still contain
    // the old complete snapshot. Keep the confirmed reply until a read has
    // acknowledged its server ID; later snapshots can then remove it normally.
    incoming.forEach((reply) => unacknowledgedReplyIds.current.delete(reply.id));
    setReplies((current) => {
      const retained = current.filter((reply) => (pendingReplyIds.current.has(reply.id) || unacknowledgedReplyIds.current.has(reply.id)) && !hidden.has(reply.id));
      const confirmed = complete || removals
        ? incoming
        : [...new Map([...current.filter((reply) => !pendingReplyIds.current.has(reply.id)), ...incoming].map((reply) => [reply.id, reply])).values()];
      const known = confirmed.filter((reply) => !hidden.has(reply.id));
      const knownIds = new Set(known.map((reply) => reply.id));
      return [...known, ...retained.filter((reply) => !knownIds.has(reply.id))].sort((left, right) => left.createdAt - right.createdAt);
    });
    if (complete) setAllLoaded(true);
    else if (removals || count !== previous.count) setAllLoaded(false);
    previousReplySnapshot.current = { count, ids: new Set(incoming.map((reply) => reply.id)) };
  }, [take.replies, take.replyCount, hiddenIds, replySnapshot]);

  const report = async (reason: ReportReason) => {
    setReporting(false);
    // Safety is a local UX guarantee, independent of network delivery. Never
    // force a supporter to keep seeing content while a report request hangs.
    hideLocally(take.id);
    onReported(take.id, 'pending');
    const result = await postReport(take.id, reason);
    onReported(take.id, result.ok ? 'sent' : result.queued ? 'queued' : 'failed');
  };

  const loadMore = async () => {
    if (remainingReplies <= 0) return;
    if (allLoaded && hiddenServerReplies === 0) {
      setRepliesExpanded(true);
      return;
    }
    const full = await fetchReplies(take.id);
    if (full) {
      const hidden = new Set([...hiddenIds, ...localHidden(), ...deletedReplyIds.current]);
      full.forEach((reply) => unacknowledgedReplyIds.current.delete(reply.id));
      if (latestReplySnapshot.current === replySnapshot) setReplyCountAuthority({ snapshot: replySnapshot, count: full.filter((reply) => !hidden.has(reply.id)).length });
      setReplies((current) => {
        const confirmed = full.filter((reply) => !hidden.has(reply.id));
        const knownIds = new Set(confirmed.map((reply) => reply.id));
        return [...confirmed, ...current.filter((reply) => (pendingReplyIds.current.has(reply.id) || unacknowledgedReplyIds.current.has(reply.id)) && !hidden.has(reply.id) && !knownIds.has(reply.id))];
      });
      setAllLoaded(true);
    }
    setRepliesExpanded(true);
  };

  const commitReply = () => {
    if (!writesEnabled || threadReadOnly || replyPending) return;
    const text = replyText.trim().slice(0, MAX_TAKE);
    if (!text) return;
    const delivery = replyDraft.begin(text);
    if (!delivery) return;
    setReplyFailure(false);
    setReplyPending(true);
    const optimistic = buildTake(myHandle, take.thread, text, take.matchId ?? null, take.id);
    pendingReplyIds.current.add(optimistic.id);
    setReplies((prev) => [...prev, optimistic]);
    setRepliesExpanded(true);
    setReplying(false);
    const restoreDraft = () => {
      pendingReplyIds.current.delete(optimistic.id);
      setReplies((prev) => prev.filter((r) => r.id !== optimistic.id));
      setReplyText(text);
      setReplying(true);
      setReplyFailure(true);
    };
    postTake(optimistic).then(({ take: srv, blocked, rejected, handleChanged }) => {
      if (!settleNorthBankDraft(delivery, !blocked && !rejected)) return;
      if (!replyLive.current) {
        if (!blocked && !rejected) announceRecoveredNorthBankTake(srv);
        return;
      }
      if (blocked || rejected) {
        restoreDraft();
        return;
      }
      if (handleChanged) onHandleChange(handleChanged);
      pendingReplyIds.current.delete(optimistic.id);
      if (!latestReplyPreviewIds.current.has(srv.id)) unacknowledgedReplyIds.current.add(srv.id);
      else unacknowledgedReplyIds.current.delete(srv.id);
      setReplies((prev) => [...prev.filter((reply) => reply.id !== optimistic.id && reply.id !== srv.id), srv].sort((left, right) => left.createdAt - right.createdAt));
    }).catch(() => {
      if (settleNorthBankDraft(delivery, false) && replyLive.current) restoreDraft();
    }).finally(() => { if (replyLive.current) setReplyPending(false); });
    broadcastFanReaction(take.matchId ?? null, 'take');
  };

  const submitReply = () => {
    if (!writesEnabled || threadReadOnly || replyPending) return;
    if (!participationAllowed) {
      onRequireParticipation('send your reply', commitReply, () => replyTriggerRef.current?.focus());
      return;
    }
    commitReply();
  };

  return (
    <article className={`take${mine ? ' take--mine' : ''}`} data-take-id={take.id}>
      <header className="take-head">
        <span className="take-handle">
          {take.handle}
          {mine && <span className="take-you">you</span>}
        </span>
        <span className="take-time">{timeAgo(take.createdAt)}</span>
      </header>
      <p className="take-body">{take.text}</p>
      <footer className="take-foot">
        {take.terminal ? (
          <span className="take-terminal">Read-only · replies preserved</span>
        ) : (
          <>
            <button
              className={`appreciate${take.myVote === 'up' ? ' on' : ''}`}
              onClick={() => participationAllowed
                ? onAppreciate(take.id)
                : onRequireParticipation('appreciate this take', () => onAppreciate(take.id))}
              aria-label="Appreciate this take"
              disabled={!writesEnabled || threadReadOnly}
            >
              <span className="diamond">♦</span> {take.up.toLocaleString()}
            </button>
            <button ref={replyTriggerRef} className="reply-trigger" onClick={() => { if (!replyPending) setReplying((r) => !r); }} disabled={!writesEnabled || threadReadOnly} aria-disabled={replyPending || undefined}>
              Reply
            </button>
            {threadReadOnly && <span className="take-terminal">Read-only</span>}
            {mine ? (
              <>
                <button className="take-share" onClick={() => onShare(take)}>Share</button>
                <button className="take-delete" onClick={() => onDelete(take.id)}>Delete</button>
              </>
            ) : (
              <button className="report-btn" onClick={() => setReporting((r) => !r)} aria-label="Report take">⚑</button>
            )}
          </>
        )}
      </footer>
      {replyPending && <p className="composer-note" role="status">Saving reply…</p>}
      {replyDeleteFailure && <p className="composer-note" role="alert">Reply deletion did not complete. Your reply is still public — try again when COYG is available.</p>}
      {!take.terminal && reporting && (
        <div className="report-row">
          {REPORT_REASONS.map((r) => (
            <button key={r.id} className="chip chip--small" onClick={() => report(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      )}
      {(previewReplies.length > 0 || remainingReplies > 0) && (
        <div className={`replies${remainingReplies > 0 ? ' replies--has-more' : ''}`}>
          {previewReplies.map((r) => (
            <ReplyRow
              key={r.id}
              reply={r}
              mine={r.mine === true}
              onDelete={(id) => {
                setReplyDeleteFailure(false);
                void deleteTake(id).then((ok) => {
                  if (ok) {
                    hideLocally(id);
                    deletedReplyIds.current.add(id);
                    unacknowledgedReplyIds.current.delete(id);
                    if (latestReplySnapshot.current === replySnapshot) setReplyCountAuthority((current) => ({ snapshot: replySnapshot, count: Math.max(0, (current?.snapshot === replySnapshot ? current.count : serverCount) - 1) }));
                    setReplies((prev) => prev.filter((x) => x.id !== id));
                  } else setReplyDeleteFailure(true);
                }).catch(() => setReplyDeleteFailure(true));
              }}
              onReport={(id, reason) => {
                hideLocally(id);
                setReplies((prev) => prev.filter((reply) => reply.id !== id));
                onReported(id, 'pending');
                void postReport(id, reason).then((result) => {
                  onReported(id, result.ok ? 'sent' : result.queued ? 'queued' : 'failed');
                });
              }}
              participationAllowed={participationAllowed}
              writesEnabled={writesEnabled && !threadReadOnly}
              onRequireParticipation={onRequireParticipation}
            />
          ))}
          {remainingReplies > 0 && (
            <button className="more-replies" onClick={loadMore} aria-expanded={repliesExpanded}>
              <span className="more-replies-count">+{remainingReplies}</span>
              <span>{moreLabel}</span>
            </button>
          )}
        </div>
      )}
      {Boolean(take.moderatedReplyCount) && (
        <div className="modnote modnote--reply">
          <span>⚑ {take.moderatedReplyCount} {take.moderatedReplyCount === 1 ? 'reply was' : 'replies were'} withheld after reports or moderation.</span>
          <span className="why" title="We keep this note so moderated replies do not disappear silently.">why?</span>
        </div>
      )}
      {!take.terminal && writesEnabled && !threadReadOnly && replying && (
        <div className="reply-composer">
          <div className="composer-note">Reply to {take.handle}</div>
          {replyFailure && <p className="composer-note" role="alert">Your reply wasn’t saved. Your draft is kept; wait a moment and try again when posting is available.</p>}
          {replyDraft.capacityBlocked && <p className="composer-note" role="status">Other contributions are still saving. Your draft is kept; try again in a moment.</p>}
          <div className="reply-composer-row">
            <input
              className="composer-input"
              aria-label={`Reply to ${take.handle}`}
              autoFocus
              autoComplete="off"
              autoCapitalize="sentences"
              enterKeyHint="send"
              value={replyText}
              maxLength={MAX_TAKE}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) submitReply();
                if (e.key === 'Escape') setReplying(false);
              }}
              placeholder="Write a reply…"
            />
            <button
              className={`reply-send${replyText.trim() ? ' ready' : ''}`}
              onClick={submitReply}
              disabled={!replyText.trim()}
              aria-label="Send reply"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

/** Appreciation toggle on a top-level take (one positive signal, no downvote). */
function applyAppreciation(takes: Take[], id: string): Take[] {
  return takes.map((t) => {
    if (t.id !== id) return t;
    const was = t.myVote === 'up';
    return { ...t, myVote: was ? null : 'up', up: Math.max(0, was ? t.up - 1 : t.up + 1) };
  });
}

// ── Screen ───────────────────────────────────────────────────────────────────
export type Room = 'all' | 'match' | 'TRANSFERS' | 'SEASON' | 'ANALYSIS';

const COMMUNITY_LIVE_CHECK_MS = 30_000;
const COMMUNITY_INITIAL_CHECK_MS = 30_000;

export function takeBelongsToRoom(take: Take, room: Room, activeMatchId: string | null): boolean {
  if (take.parentId) return false;
  if (room === 'all') return true;
  if (room === 'match') return take.matchId === activeMatchId;
  return take.thread === room;
}

export function selectQueuedRoomTakes(
  incoming: Take[],
  current: Take[],
  room: Room,
  activeMatchId: string | null,
  readerScrollTop: number,
  hasCompletedInitialCheck: boolean,
): Take[] {
  if (!hasCompletedInitialCheck || readerScrollTop <= 80) return [];
  const currentIds = new Set(current.map((take) => take.id));
  return incoming.filter((take) => !currentIds.has(take.id) && takeBelongsToRoom(take, room, activeMatchId));
}

export function reconcileTakeRefresh(
  incoming: Take[],
  current: Take[],
  queued: Take[],
  moderatedIds: Set<string>,
  room: Room,
  activeMatchId: string | null,
  readerScrollTop: number,
  hasCompletedInitialCheck: boolean,
): { visible: Take[]; queued: Take[] } {
  const incomingIds = new Set(incoming.map((take) => take.id));
  const retained = queued.filter((take) => incomingIds.has(take.id) && !moderatedIds.has(take.id));
  const retainedIds = new Set(retained.map((take) => take.id));
  const fresh = selectQueuedRoomTakes(incoming, current, room, activeMatchId, readerScrollTop, hasCompletedInitialCheck)
    .filter((take) => !moderatedIds.has(take.id) && !retainedIds.has(take.id));
  const nextQueued = [...fresh, ...retained];
  const heldIds = new Set(nextQueued.map((take) => take.id));
  return {
    visible: incoming.filter((take) => !moderatedIds.has(take.id) && !heldIds.has(take.id)),
    queued: nextQueued,
  };
}

export function focusRevealedTake(feed: HTMLElement | null, takeId: string): void {
  if (!feed) return;
  feed.scrollTo?.({ top: 0, behavior: 'smooth' });
  const target = [...feed.querySelectorAll<HTMLElement>('[data-take-id]')]
    .find((element) => element.dataset.takeId === takeId);
  if (!target) {
    feed.focus({ preventScroll: true });
    return;
  }
  target.tabIndex = -1;
  target.focus({ preventScroll: true });
}

export function ownTakeShareMoment(take: Take, room: Room, threadOpponent: string): ShareMoment {
  const shareRoom = take.thread === 'MATCH' && take.matchId ? 'match' : room;
  const roomLabel = shareRoom === 'all'
    ? 'North Bank'
    : shareRoom === 'match'
      ? room === 'match' ? threadOpponent : 'Matchday'
      : shareRoom.charAt(0) + shareRoom.slice(1).toLowerCase();
  return {
    title: 'My North Bank take — COYG',
    text: take.text,
    url: northBankLink(shareRoom, shareRoom === 'match' ? take.matchId : undefined),
    card: {
      eyebrow: 'MY NORTH BANK TAKE',
      headline: take.text,
      detail: `${take.handle} · ${roomLabel}`,
      personal: true,
    },
  };
}

export const TerraceScreen = ({
  threadMatchId,
  selectedRoom,
  onOpenLore,
  onRoomChange,
  safetyRequested,
}: {
  threadMatchId: string | null;
  /** A room requested from elsewhere, such as a future deep-linked topic. */
  selectedRoom?: Room | null;
  onOpenEntry?: (id: string) => void;
  onOpenLore?: (matchId: string) => void;
  onRoomChange?: (room: Room) => void;
  /** Opens the private care sheet from a canonical /north-bank route. */
  safetyRequested?: boolean;
}) => {
  const match = useFeed(FEEDS.match);
  const fixtures = useFeed(FEEDS.fixtures);
  const lore = useFeed(FEEDS.lore);
  const nextThreadFixture = useMemo(() => {
    const now = Date.now();
    return fixtures.data.fixtures
      .filter((f): f is Fixture & { kickoffUtc: string } => f.kickoffUtc != null && f.statusQuality !== 'overdue' && f.status !== 'postponed' && f.status !== 'ft' && Date.parse(f.kickoffUtc) > now - 90 * 60_000)
      .sort((a, b) => Date.parse(a.kickoffUtc) - Date.parse(b.kickoffUtc))[0] ?? null;
  }, [fixtures.data.fixtures]);
  const activeMatchId = threadMatchId ?? (match.data.phase === 'ft' ? nextThreadFixture?.id : null) ?? match.data.matchId;
  const matchOn = !match.stale && (match.data.phase === 'live' || match.data.phase === 'ht');
  // The thread is scoped to a SPECIFIC match — resolve its identity from the
  // fixtures feed (falling back to the live match) so the header names the
  // match you actually opened, not whatever is live.
  const threadFixture = fixtures.data.fixtures.find((f) => f.id === activeMatchId);
  const threadOpponent = threadFixture?.opponent ?? (activeMatchId === match.data.matchId ? match.data.opponent : 'fixture awaiting confirmation');
  const threadAway = threadFixture
    ? arsenalIsAway(threadFixture.venue, threadFixture.arsenalSide)
    : arsenalIsAway(match.data.venue, match.data.arsenalSide);
  const threadFinished = Boolean(activeMatchId && (
    threadFixture?.status === 'ft' ||
    (activeMatchId === match.data.matchId && match.data.phase === 'ft')
  ));
  // Match participation follows the same current/next programme boundary as
  // the server. A visible later fixture is readable, not a promise it can accept
  // posts. Unknown, postponed and untimed fixtures cannot open a composer.
  const threadIsCurrent = activeMatchId === match.data.matchId;
  const nextContributionFixture = fixtures.data.fixtures
    .filter((fixture): fixture is Fixture & { kickoffUtc: string } => fixture.kickoffUtc != null && Date.parse(fixture.kickoffUtc) > Date.parse(match.data.kickoffUtc ?? ''))
    .sort((left, right) => Date.parse(left.kickoffUtc) - Date.parse(right.kickoffUtc))[0];
  const threadIsNext = match.data.phase === 'ft' && activeMatchId === nextContributionFixture?.id;
  const threadKickoff = Date.parse(threadFixture?.kickoffUtc ?? '');
  const threadWritable = Boolean(
    activeMatchId && threadFixture && !threadFinished &&
    !threadFixture.timeTbc && threadFixture.status !== 'postponed' &&
    Number.isFinite(threadKickoff) && match.data.matchStatus !== 'postponed' &&
    !(threadIsCurrent && match.data.communityReport) &&
    ((threadIsCurrent && (match.data.phase === 'live' || match.data.phase === 'ht') && !match.stale) ||
      ((threadIsCurrent && (match.data.phase === 'idle' || match.data.phase === 'pre') || threadIsNext) && threadKickoff > Date.now())),
  );
  const threadClosed = !threadWritable;

  const [room, setRoom] = useState<Room>(
    // Default to 'all' (general chat). Only jump to 'match' when the user arrived
    // via an explicit match deep-link (threadMatchId) or when a match is actually
    // live/HT right now (matchOn). A future fixture being visible (nextThreadFixture)
    // is NOT sufficient — defaulting to 'match' pre-match hides the general chat feed
    // (general takes have matchId:null and are filtered out in 'match' room).
    selectedRoom ?? (threadMatchId || matchOn ? 'match' : 'all'),
  );
  const [takes, setTakes] = useState<Take[]>(fallbackTakes);
  const [stubs, setStubs] = useState<TakeStub[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(localHidden);
  const draft = useNorthBankDraft(room, activeMatchId);
  const input = draft.text;
  const setInput = draft.setText;
  const [localTakePending, setTakePending] = useState(false);
  const takePending = localTakePending || draft.pending;
  const takePendingRef = useRef(false);
  takePendingRef.current = takePending;
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeAction, setNoticeAction] = useState<'safety' | null>(null);
  const live = useRef(true);
  // Set once the user appreciates/composes. An OFFLINE fetch landing after that
  // must not clobber optimistic state with the stale cache; a real server
  // response remains the tie-breaker.
  const dirty = useRef(false);
  const writeGeneration = useRef(0);
  const loadGeneration = useRef(0);
  const deletedIds = useRef(new Set<string>());
  const appreciationGenerations = useRef(new Map<string, number>());
  const confirmedAppreciations = useRef(new Map<string, { up: number; myVote: Take['myVote'] }>());
  const onRoomChangeRef = useRef(onRoomChange);
  const [myHandle, setMyHandle] = useState(getHandle());
  const [participationAllowed, acceptParticipation] = useParticipationConsent();
  const [gateOpen, setGateOpen] = useState(false);
  const [gateAction, setGateAction] = useState('join in');
  const pendingParticipationAction = useRef<(() => void) | null>(null);
  const pendingParticipationFocus = useRef<(() => void) | null>(null);
  const composeInputRef = useRef<HTMLInputElement>(null);
  const takeFeedRef = useRef<HTMLDivElement>(null);
  const roomStripRef = useRef<HTMLDivElement>(null);
  const takesRef = useRef(takes);
  const initialTakeCheck = useRef(false);
  const [queuedTakes, setQueuedTakes] = useState<Take[]>([]);
  const queuedTakesRef = useRef<Take[]>([]);
  const [serverLatestActivityAt, setServerLatestActivityAt] = useState<number | null>(null);
  const [publicTakesLastHour, setPublicTakesLastHour] = useState<number | null>(null);
  const [safetyOpen, setSafetyOpen] = useState(() => safetyRequested === true || new URLSearchParams(window.location.search).get('safety') === '1' || window.location.hash === '#safety-appeal');
  const [safetyInitialKind, setSafetyInitialKind] = useState<SafetyRequestKind>('complaint');
  const [safetyDeliveryNotice, setSafetyDeliveryNotice] = useState<string | null>(null);
  const [safetyPending, setSafetyPending] = useState(0);
  const [safetyRequests, setSafetyRequests] = useState<SafetyRequest[]>([]);
  const [failedSafetyRequests, setFailedSafetyRequests] = useState<FailedSafetyDelivery[]>(() => readFailedSafetyDeliveries());
  const [participationStatus, setParticipationStatus] = useState<ParticipationStatus | null>(null);
  const { connection, checkedAt, recordCheck } = useCommunityAccess();

  useEffect(() => {
    onRoomChangeRef.current = onRoomChange;
  }, [onRoomChange]);

  useEffect(() => {
    const showFailure = () => {
      void consumeFailedReportDeliveries().then((count) => { if (count > 0) {
        setNotice('A queued report could not be delivered. It remains hidden for you.');
        setNoticeAction('safety');
      } });
    };
    showFailure();
    window.addEventListener('coyg:report-delivery-failed', showFailure);
    return () => window.removeEventListener('coyg:report-delivery-failed', showFailure);
  }, []);

  useEffect(() => {
    const refreshFailures = () => {
      setFailedSafetyRequests(readFailedSafetyDeliveries());
      setSafetyDeliveryNotice('A queued private request was not delivered. Its reference and text are saved here so you can try again.');
      setNotice('A private request needs another attempt.');
      setNoticeAction('safety');
    };
    window.addEventListener('coyg:safety-delivery-failed', refreshFailures);
    return () => window.removeEventListener('coyg:safety-delivery-failed', refreshFailures);
  }, []);

  useEffect(() => {
    takesRef.current = takes;
  }, [takes]);

  useEffect(() => {
    queuedTakesRef.current = queuedTakes;
  }, [queuedTakes]);

  useEffect(() => subscribeToRecoveredNorthBankTakes((take) => {
    if (!takeBelongsToRoom(take, room, activeMatchId)) return;
    dirty.current = true;
    writeGeneration.current += 1;
    setTakes((current) => current.some((row) => row.id === take.id) ? current : [take, ...current]);
  }), [room, activeMatchId]);

  useEffect(() => {
    if (safetyRequested) setSafetyOpen(true);
  }, [safetyRequested]);

  useEffect(() => {
    const openLegacySafetyLink = () => {
      if (window.location.hash === '#safety-appeal' || new URLSearchParams(window.location.search).get('safety') === '1') {
        setSafetyOpen(true);
      }
    };
    window.addEventListener('hashchange', openLegacySafetyLink);
    return () => window.removeEventListener('hashchange', openLegacySafetyLink);
  }, []);

  useEffect(() => {
    live.current = true;
    initialTakeCheck.current = false;
    queuedTakesRef.current = [];
    setQueuedTakes([]);
    let timer: number | null = null;
    let unchangedChecks = 0;
    let lastFingerprint = '';
    let activeController: AbortController | null = null;
    let lastCheck = -Infinity;
    let retryAt = 0;
    const schedule = () => {
      if (!live.current || !mayPoll()) return;
      const delay = matchOn
        ? COMMUNITY_LIVE_CHECK_MS
        : unchangedChecks >= 6
          ? 300_000
          : unchangedChecks >= 3
            ? 60_000
            : COMMUNITY_INITIAL_CHECK_MS;
      timer = window.setTimeout(refresh, Math.max(delay, retryAt - Date.now()));
    };
    const refresh = () => {
      if (!mayPoll()) return;
      if (timer !== null) window.clearTimeout(timer);
      if (Date.now() < Math.max(lastCheck + 30_000, retryAt)) { schedule(); return; }
      lastCheck = Date.now();
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      const load = ++loadGeneration.current;
      const generation = writeGeneration.current;
      void fetchTakes({ room, matchId: room === 'match' ? activeMatchId : null, signal: controller.signal }).then(({ takes, stubs, activity, online, retryAfterMs }) => {
        if (controller.signal.aborted || !live.current || load !== loadGeneration.current) return;
        retryAt = Date.now() + (retryAfterMs ?? (online ? 0 : 60_000));
        const fingerprint = `${activity.latestVisibleAt ?? 0}:${takes.map((take) => `${take.id}:${take.up}`).join(',')}:${stubs.map((stub) => stub.id).join(',')}`;
        unchangedChecks = online && fingerprint === lastFingerprint ? unchangedChecks + 1 : 0;
        if (online) lastFingerprint = fingerprint;
        recordCheck(online);
        setStubs(stubs);
        setServerLatestActivityAt(activity.latestVisibleAt);
        setPublicTakesLastHour(online ? activity.publicTakesLastHour : null);
        if (generation !== writeGeneration.current) {
          setTakes((current) => {
            const currentIds = new Set(current.map((take) => take.id));
            return [...current, ...takes.filter((take) => !currentIds.has(take.id) && !deletedIds.current.has(take.id))];
          });
          return;
        }
        if (!online && dirty.current) return;
        const incoming = takes.filter((take) => !deletedIds.current.has(take.id));
        const moderatedIds = new Set(stubs.map((stub) => stub.id));
        const current = takesRef.current;
        const reconciled = reconcileTakeRefresh(
          incoming,
          current,
          queuedTakesRef.current,
          moderatedIds,
          room,
          activeMatchId,
          takeFeedRef.current?.scrollTop ?? 0,
          initialTakeCheck.current,
        );
        queuedTakesRef.current = reconciled.queued;
        setQueuedTakes(reconciled.queued);
        setTakes(reconciled.visible);
        initialTakeCheck.current = true;
      }).finally(() => { if (!controller.signal.aborted) schedule(); });
    };
    refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
      else {
        if (timer !== null) window.clearTimeout(timer);
        activeController?.abort();
      }
    };
    const onOnline = () => refresh();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      live.current = false;
      if (timer !== null) window.clearTimeout(timer);
      activeController?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, [activeMatchId, matchOn, recordCheck, room]);

  useEffect(() => {
    fetchParticipationStatus().then((status) => {
      if (live.current) setParticipationStatus(status);
    });
    fetchSafetyRequests().then((requests) => {
      if (live.current) {
        setSafetyRequests(requests);
        setSafetyPending(requests.filter((request) => request.status === 'open' || request.status === 'reviewing').length);
      }
    });
  }, []);

  useEffect(() => {
    if (threadMatchId) {
      setRoom('match');
      onRoomChangeRef.current?.('match');
    }
  }, [threadMatchId]);

  // A room requested from another surface wins on arrival; the user can still
  // switch freely afterwards.
  useEffect(() => {
    if (selectedRoom) {
      setRoom(selectedRoom);
      onRoomChangeRef.current?.(selectedRoom);
    }
  }, [selectedRoom]);

  useEffect(() => {
    const selected = roomStripRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (typeof selected?.scrollIntoView === 'function') {
      selected.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [room]);

  // Parent replies are rendered only inside their take. If an API/cache/offline
  // queue returns them in the broad take list, keep them out of the main feed.
  const visible = takes.filter((t) => !hidden.has(t.id) && !t.parentId);
  const filtered =
    room === 'all'
      ? visible
      : room === 'match'
        ? // Strictly this match's thread — no generic MATCH chatter leaking in.
          visible.filter((t) => t.matchId === activeMatchId)
        : visible.filter((t) => t.thread === room);
  const queuedForRoom = queuedTakes.filter((take) => takeBelongsToRoom(take, room, activeMatchId));
  const localLatestActivityAt = [...filtered, ...queuedForRoom].reduce<number | null>((latest, take) => {
    const createdAt = typeof take.createdAt === 'number' ? take.createdAt : Date.parse(String(take.createdAt));
    if (!Number.isFinite(createdAt)) return latest;
    return latest == null ? createdAt : Math.max(latest, createdAt);
  }, null);
  const latestActivityAt = serverLatestActivityAt == null
    ? localLatestActivityAt
    : localLatestActivityAt == null
      ? serverLatestActivityAt
      : Math.max(serverLatestActivityAt, localLatestActivityAt);

  const openSafety = (kind: SafetyRequestKind = 'complaint') => {
    setSafetyInitialKind(kind);
    setSafetyOpen(true);
    setNoticeAction(null);
  };

  const closeSafety = () => {
    setSafetyOpen(false);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('safety');
      if (url.hash === '#safety-appeal') url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    } catch { /* sandboxed history */ }
  };

  const revealQueuedTakes = () => {
    if (queuedForRoom.length === 0) return;
    const firstRevealedId = queuedForRoom[0]!.id;
    const queuedIds = new Set(queuedForRoom.map((take) => take.id));
    setTakes((current) => {
      const currentIds = new Set(current.map((take) => take.id));
      return [...queuedForRoom.filter((take) => !currentIds.has(take.id)), ...current];
    });
    setQueuedTakes((queued) => {
      const next = queued.filter((take) => !queuedIds.has(take.id));
      queuedTakesRef.current = next;
      return next;
    });
    requestAnimationFrame(() => {
      focusRevealedTake(takeFeedRef.current, firstRevealedId);
    });
  };

  const handleAppreciate = (id: string) => {
    const current = takes.find((t) => t.id === id);
    if (!current) return;
    dirty.current = true;
    writeGeneration.current += 1;
    const willBeOn = current.myVote !== 'up';
    if (!confirmedAppreciations.current.has(id)) confirmedAppreciations.current.set(id, { up: current.up, myVote: current.myVote });
    const generation = (appreciationGenerations.current.get(id) ?? 0) + 1;
    appreciationGenerations.current.set(id, generation);
    setTakes((prev) => applyAppreciation(prev, id));
    appreciate(id, willBeOn).then((res) => {
      if (res) confirmedAppreciations.current.set(id, res);
      if (live.current && appreciationGenerations.current.get(id) === generation) {
        const confirmed = confirmedAppreciations.current.get(id)!;
        setTakes((prev) => prev.map((t) => (t.id === id ? { ...t, ...confirmed } : t)));
        if (!res) setNotice('Appreciation wasn’t saved. Try again when COYG is available.');
      }
    });
  };

  const requireParticipation = (action: string, onComplete: () => void, onSuccessFocus?: () => void) => {
    pendingParticipationAction.current = onComplete;
    pendingParticipationFocus.current = onSuccessFocus ?? null;
    setGateAction(action);
    setGateOpen(true);
  };

  const closeParticipationGate = () => {
    pendingParticipationAction.current = null;
    pendingParticipationFocus.current = null;
    setGateOpen(false);
  };

  const handleReported = (id: string, delivery: 'pending' | 'sent' | 'queued' | 'failed') => {
    // Once a supporter reports something, their local hide is permanent even
    // if delivery later fails. The safety route remains available for retry.
    setHidden((prev) => new Set(prev).add(id));
    setNoticeAction(delivery === 'failed' ? 'safety' : null);
    setNotice(delivery === 'pending'
      ? 'Sending report — it’s already hidden for you.'
      : delivery === 'sent'
      ? 'Reported — thanks. It’s hidden for you.'
      : delivery === 'queued'
        ? 'Report queued — it remains hidden for you and will send when the service recovers.'
        : 'Couldn’t send that report — please try again or use Safety, complaints & appeals.');
    setTimeout(() => setNotice(null), delivery === 'pending' ? 4000 : 2400);
  };

  const handleDelete = (id: string) => {
    deleteTake(id).then((ok) => {
      if (!live.current) return;
      if (ok) {
        dirty.current = true;
        writeGeneration.current += 1;
        deletedIds.current.add(id);
        setTakes((prev) => prev.filter((t) => t.id !== id));
      } else {
        setNoticeAction(null);
        setNotice('That take is still public — deletion did not complete. Please try again.');
      }
    });
  };

  const commitTake = () => {
    if (takePendingRef.current) return;
    if (participationStatus?.writesEnabled === false) {
      setNoticeAction(participationStatus.sanction ? 'safety' : null);
      setNotice(participationStatus.sanction ? 'Participation is restricted on this device. Appeals remain open.' : 'Community posting is temporarily paused.');
      return;
    }
    if (room === 'match' && threadClosed) {
      setNoticeAction(null);
      setNotice('This match thread is read-only. Join All to discuss upcoming fixtures.');
      return;
    }
    const text = input.trim().slice(0, MAX_TAKE);
    if (!text) return;
    const delivery = draft.begin(text);
    if (!delivery) return;
    takePendingRef.current = true;
    setTakePending(true);
    dirty.current = true;
    writeGeneration.current += 1;
    const thread: Thread =
      room === 'ANALYSIS' || room === 'TRANSFERS' || room === 'SEASON' ? room : 'MATCH';
    const matchId = room === 'match' ? activeMatchId : null;
    const optimistic = buildTake(myHandle, thread, text, matchId);
    setTakes((prev) => [optimistic, ...prev]);
    const restoreDraft = () => {
      setTakes((prev) => prev.filter((t) => t.id !== optimistic.id));
      setInput(text);
    };
    postTake(optimistic).then(({ take, blocked, rejected, handleChanged }) => {
      // Navigation can unmount this screen while saving. Settle its memory
      // draft first so returning cannot replay a successful or pending take.
      if (!settleNorthBankDraft(delivery, !blocked && !rejected)) return;
      if (!live.current) {
        if (!blocked && !rejected) announceRecoveredNorthBankTake(take);
        return;
      }
      if (blocked || rejected) {
        // Server reached + refused — drop the optimistic take (never strand it).
        restoreDraft();
        setNoticeAction(rejected === 'banned' || rejected === 'suspended' ? 'safety' : null);
        setNotice(
          blocked
            ? 'That language isn’t for the North Bank.'
            : rejected === 'closed'
              ? 'This match is in the book — the thread is read-only.'
              : rejected === 'cooldown'
                ? 'Easy — give it a moment before the next one.'
                : rejected === 'consent-required'
                  ? 'Confirm the North Bank rules before posting.'
                : rejected === 'handle-conflict'
                  ? 'That supporter name collided — choose another and try again.'
                  : rejected === 'banned'
                    ? 'This device is banned from community participation. Appeals remain open below.'
                    : rejected === 'suspended'
                      ? 'This device is temporarily suspended. Appeals remain open below.'
                      : rejected === 'community-paused'
                        ? 'Community posting is paused by the owner. Reading and appeals remain open.'
                : 'Couldn’t post that — try again.',
        );
        setTimeout(() => setNotice(null), 2800);
        return;
      }
      if (handleChanged) setMyHandle(handleChanged);
      setInput('');
      setTakes((prev) =>
        prev.some((t) => t.id === optimistic.id)
          ? prev.map((t) => (t.id === optimistic.id ? take : t))
          : [take, ...prev],
      );
    }).catch(() => {
      if (!settleNorthBankDraft(delivery, false)) return;
      if (!live.current) return;
      restoreDraft();
      setNoticeAction(null);
      setNotice('Your take wasn’t saved. Your draft is still here — try again when COYG is available.');
    }).finally(() => {
      takePendingRef.current = false;
      if (live.current) setTakePending(false);
    });
    broadcastFanReaction(matchId, 'take');
  };

  const handleSubmit = () => {
    if (takePendingRef.current) return;
    if (participationStatus?.writesEnabled === false) {
      setNoticeAction(participationStatus.sanction ? 'safety' : null);
      setNotice(participationStatus.sanction ? 'Participation is restricted on this device. Appeals remain open.' : 'Community posting is temporarily paused.');
      return;
    }
    if (!participationAllowed) {
      requireParticipation('post your take', commitTake, () => composeInputRef.current?.focus());
      return;
    }
    commitTake();
  };

  const rooms: Array<{ id: Room; label: string; hot?: boolean }> = [
    { id: 'all', label: 'All' },
    { id: 'match', label: matchOn ? 'Match thread' : 'Matchday', hot: matchOn },
    { id: 'TRANSFERS', label: 'Transfers' },
    { id: 'SEASON', label: 'Season' },
    { id: 'ANALYSIS', label: 'Analysis' },
  ];

  // Fixture/live lifecycle closes posting. Lore is an optional archive link,
  // never the authority for whether a thread is writable.
  const bookEntry =
    room === 'match' && activeMatchId
      ? lore.data.entries.find((e) => e.matchId === activeMatchId) ?? null
      : null;

  return (
    <div className="screen terrace-screen" data-mobile-chat>
      <div className="screen-head terrace-head">
        <div>
          <h1 className="screen-title">North Bank</h1>
          <p className="screen-sub">Learn, watch, discuss.</p>
        </div>
        <HandleEditor
          current={myHandle}
          onChange={setMyHandle}
          participationAllowed={participationAllowed}
          onRequireParticipation={requireParticipation}
        />
      </div>

      <div ref={roomStripRef} className="chip-strip terrace-room-strip" role="tablist" aria-label="Rooms">
        {rooms.map((r) => (
          <button
            key={r.id}
            className={`chip${room === r.id ? ' active' : ''}${r.hot ? ' chip--hot' : ''}`}
            onClick={() => {
              setRoom(r.id);
              onRoomChangeRef.current?.(r.id);
            }}
            role="tab"
            aria-selected={room === r.id}
          >
            {r.hot && <span className="live-dot pulse" />}
            {r.label}
          </button>
        ))}
      </div>

      <div className="terrace-utility-row">
        <CommunityActivityStatus
          connection={connection}
          checkedAt={checkedAt}
          latestActivityAt={latestActivityAt}
          publicTakesLastHour={publicTakesLastHour}
          checkIntervalSeconds={30}
          adaptive={!matchOn}
        />
        <button type="button" className="safety-entry" onClick={() => openSafety()}>
          Help &amp; safety
          {safetyPending + failedSafetyRequests.length > 0 ? (
            <span className="safety-entry-count" aria-label={`${safetyPending + failedSafetyRequests.length} safety items need attention`}>
              {safetyPending + failedSafetyRequests.length}
            </span>
          ) : null}
        </button>
      </div>

      {participationStatus && !participationStatus.writesEnabled && (
        <div className="participation-status" role="status">
          <strong>{participationStatus.sanction?.kind === 'banned' ? 'Participation banned' : participationStatus.sanction ? 'Participation suspended' : 'Community writes paused'}</strong>
          <span>{participationStatus.sanction?.reason ?? participationStatus.community.reason}</span>
          {participationStatus.sanction?.expiresAt ? <span>Until {new Date(participationStatus.sanction.expiresAt).toLocaleString('en-GB')}</span> : null}
          {participationStatus.sanction ? (
            <a href="#safety-appeal" onClick={(event) => { event.preventDefault(); openSafety('appeal'); }}>Appeal this decision</a>
          ) : null}
        </div>
      )}

      {room === 'match' && (
        <Rule
          label={!threadFixture && !threadIsCurrent ? 'Match details unavailable' : `Arsenal ${threadAway ? '@' : 'v'} ${threadOpponent}`}
          accent="#EF0107"
        />
      )}

      {bookEntry && (
        <InTheBookCard entry={bookEntry} onOpenLore={(id) => onOpenLore?.(id)} />
      )}

      {notice ? (
        <StatusToast
          message={notice}
          actionLabel={noticeAction === 'safety' ? 'Open help' : undefined}
          onAction={noticeAction === 'safety' ? () => openSafety(participationStatus?.sanction ? 'appeal' : 'complaint') : undefined}
          urgent={notice.includes('still public')}
        />
      ) : null}

      {room === 'TRANSFERS' && (
        <div className="room-prompt room-prompt--transfers">
          Transfers — talk what&apos;s confirmed. Rumour ≠ fact.
        </div>
      )}

      {room === 'SEASON' && (
        <div className="room-prompt">
          Season — how are we tracking?
        </div>
      )}

      {queuedForRoom.length > 0 ? (
        <div className="new-takes-bar" aria-live="polite">
          <button type="button" className="new-takes-reveal" onClick={revealQueuedTakes}>
            Show {queuedForRoom.length} new {queuedForRoom.length === 1 ? 'take' : 'takes'}
          </button>
        </div>
      ) : null}

      {!bookEntry && (
      <div ref={takeFeedRef} className="take-feed terrace-feed" role="region" tabIndex={0} aria-label="North Bank takes">
        {filtered.map((take) => (
          <TakeCard
            key={take.id}
            take={take}
            mine={take.mine === true}
            myHandle={myHandle}
            onAppreciate={handleAppreciate}
            onReported={handleReported}
            onDelete={handleDelete}
            onShare={(ownedTake) => {
              void shareMoment(ownTakeShareMoment(ownedTake, room, threadOpponent)).then((result) => {
                if (result === 'copied') setNotice('Your take and North Bank link were copied.');
                if (result === 'failed') setNotice('Sharing is unavailable on this device right now.');
              });
            }}
            hiddenIds={hidden}
            participationAllowed={participationAllowed}
            writesEnabled={participationStatus?.writesEnabled ?? true}
            threadReadOnly={room === 'match' && threadClosed}
            onRequireParticipation={requireParticipation}
            onHandleChange={setMyHandle}
          />
        ))}
        {stubs.length > 0 ? (
          <details className="modnote modnote-group">
            <summary>{stubs.length} {stubs.length === 1 ? 'item' : 'items'} withheld · Why?</summary>
            <p>Reported takes are hidden while reviewed. This quiet marker keeps moderation visible without repeating private case detail.</p>
          </details>
        ) : null}
        {filtered.length === 0 && stubs.length === 0 && (
          room === 'match' && threadClosed ? (
            <EmptyNote>{threadFinished ? 'No takes were posted before this match thread closed.' : 'No takes have been published for this match thread.'}</EmptyNote>
          ) : (
            <div className="terrace-empty">
              <span className="terrace-empty-kicker">North Bank question</span>
              <h2>{room === 'match' ? `What changes the game against ${threadOpponent}?` : room === 'TRANSFERS' ? 'What is confirmed—and what is only noise?' : room === 'ANALYSIS' ? 'What did you see that the score missed?' : room === 'SEASON' ? 'What is the season telling us so far?' : 'What is on your Arsenal mind?'}</h2>
              <p>No fabricated activity and no forced sign-up. Read freely; add the first considered take when you are ready.</p>
            </div>
          )
        )}
      </div>
      )}

      {room === 'match' && threadClosed ? (
        <div className="compose-hint compose-hint--closed">
          {threadFinished
            ? <>This match is closed and read-only{bookEntry ? ' — read it in the match archive, or join the North Bank.' : '. No public archive has been published for it.'}</>
            : 'This match thread is read-only. Posting opens for the confirmed current or next match. Join All to discuss upcoming fixtures.'}
        </div>
      ) : (
        <div className="compose terrace-composer" aria-busy={takePending || undefined}>
          <span className="compose-handle">{shortHandle(myHandle)}</span>
            <input
              ref={composeInputRef}
              className="compose-input"
              aria-label="Add a take to the North Bank"
              autoComplete="off"
              autoCapitalize="sentences"
              enterKeyHint="send"
              value={input}
              maxLength={MAX_TAKE}
              readOnly={takePending}
              aria-disabled={takePending || undefined}
              aria-describedby={takePending ? 'north-bank-saving-take' : draft.failed || draft.capacityBlocked ? 'north-bank-kept-draft' : undefined}
              onChange={(e) => { if (!takePendingRef.current) setInput(e.target.value); }}
              onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && handleSubmit()}
              placeholder="What’s on your Arsenal mind?"
            />
            {input.length > MAX_TAKE - 60 && (
              <span className={`compose-count${input.length >= MAX_TAKE ? ' compose-count--max' : ''}`}>
                {MAX_TAKE - input.length}
              </span>
            )}
            <button
              className={`compose-send${input.trim() ? ' ready' : ''}`}
              onClick={handleSubmit}
              aria-label="Post take"
              disabled={takePending || participationStatus?.writesEnabled === false || !input.trim()}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
              </svg>
            </button>
        </div>
      )}
      {takePending && <p id="north-bank-saving-take" className="composer-note" role="status">Saving take…</p>}
      {(draft.failed || draft.capacityBlocked) && !takePending && !(room === 'match' && threadClosed) && <p id="north-bank-kept-draft" className="composer-note" role="status">{draft.capacityBlocked ? 'Other contributions are still saving. Your draft is kept; try again in a moment.' : 'Draft kept — your take wasn’t saved. Try again when posting is available.'}</p>}
      {gateOpen && (
        <ParticipationGate
          action={gateAction}
          onAccepted={acceptParticipation}
          onComplete={() => pendingParticipationAction.current?.()}
          onSuccessFocus={pendingParticipationFocus.current ?? undefined}
          onClose={closeParticipationGate}
        />
      )}
      {safetyOpen ? (
        <SafetySheet
          open
          onClose={closeSafety}
          initialKind={safetyInitialKind}
          participationStatus={participationStatus}
          requests={safetyRequests}
          failedRequests={failedSafetyRequests}
          deliveryNotice={safetyDeliveryNotice}
          onRequestsChange={(requests) => {
            setSafetyRequests(requests);
            setSafetyPending(requests.filter((request) => request.status === 'open' || request.status === 'reviewing').length);
          }}
          onFailedRequestsChange={setFailedSafetyRequests}
        />
      ) : null}
    </div>
  );
};
